/**
 * Taking a workspace's applications out as an archive, and reading one back in.
 *
 * Both halves are jobs -- an export walks every row of every table, and an
 * import writes them all -- so neither is waited for here. What this dialog
 * does is start one and say where the answer will appear.
 *
 * **Exporting and importing are not symmetric, and the interface says so.** An
 * export offers a choice of applications; an import does not, because nothing
 * reads an uploaded archive's contents before it runs -- there is no endpoint
 * that lists what is inside one, so offering a choice would mean offering a
 * list nobody can see.
 */
import { useState } from 'react'
import { Download, Trash2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Application, UserWorkspace } from '@/api/types'
import { useApplications } from '@/features/application/queries'
import { byteSize } from '@/shared/lib/bytes'
import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { exportBody, exportFile, isExportRunning } from '../transfer'
import {
  useExportWorkspace,
  useImportWorkspace,
  useUploadImport,
  useWorkspaceExports,
} from '../transfer-queries'

export function TransferDialog({
  workspace,
  open,
  onOpenChange,
}: {
  workspace: UserWorkspace
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{t('transfer.title')}</DialogTitle>
          <DialogDescription>
            {t('transfer.description', { name: workspace.name })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="export" className="flex min-h-0 flex-1 flex-col">
          <TabsList>
            <TabsTrigger value="export">{t('transfer.exportTab')}</TabsTrigger>
            <TabsTrigger value="import">{t('transfer.importTab')}</TabsTrigger>
          </TabsList>
          <TabsContent value="export" className="min-h-0 flex-1 overflow-y-auto">
            <ExportPanel workspaceId={workspace.id} />
          </TabsContent>
          <TabsContent value="import" className="min-h-0 flex-1 overflow-y-auto">
            <ImportPanel workspaceId={workspace.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function ExportPanel({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const { data: applications, isPending } = useApplications(workspaceId)
  const exports = useWorkspaceExports(workspaceId)
  const start = useExportWorkspace(workspaceId)

  // Everything, until somebody says otherwise. That is what "export this
  // workspace" means, and it is also the request that keeps meaning it as
  // applications are added -- see `exportBody`.
  const [chosen, setChosen] = useState<number[] | null>(null)
  const [onlyStructure, setOnlyStructure] = useState(false)

  const all = (applications ?? []).map((application: Application) => application.id)
  const selected = chosen ?? all

  if (isPending) return <Skeleton className="h-40" />

  return (
    <div className="space-y-4 py-2">
      <ul aria-label={t('transfer.whatToExport')} className="space-y-2">
        {(applications ?? []).map((application: Application) => (
          <li key={application.id} className="flex items-center gap-2">
            <Checkbox
              id={`export-${application.id}`}
              checked={selected.includes(application.id)}
              onCheckedChange={(on) =>
                setChosen(
                  on === true
                    ? [...selected, application.id]
                    : selected.filter((id) => id !== application.id),
                )
              }
            />
            <Label htmlFor={`export-${application.id}`} className="font-normal">
              {application.name}
            </Label>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between">
        <Label htmlFor="only-structure" className="text-sm font-normal">
          {t('transfer.onlyStructure')}
        </Label>
        <Switch
          id="only-structure"
          checked={onlyStructure}
          onCheckedChange={setOnlyStructure}
        />
      </div>

      <Button
        disabled={selected.length === 0 || start.isPending}
        onClick={() => start.mutate(exportBody(selected, all, onlyStructure))}
      >
        <Download />
        {t('transfer.export')}
      </Button>

      <Separator />

      <section aria-label={t('transfer.archives')} className="space-y-2">
        <h3 className="text-sm font-medium">{t('transfer.archives')}</h3>
        {(exports.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('transfer.noArchives')}</p>
        ) : (
          <ul className="space-y-2">
            {(exports.data ?? []).map((job) => {
              const file = exportFile(job)
              return (
                <li
                  key={job.id}
                  className="flex items-center justify-between gap-2 rounded border p-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm">
                      {file?.name ?? t('transfer.beingMade')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {whenText(job.created_on)}
                      {job.state === 'failed' && (
                        <> · {job.human_readable_error || t('transfer.exportFailed')}</>
                      )}
                    </p>
                  </div>
                  {/*
                    Rendered as a link rather than a button that fetches: the url
                    is signed and served by the backend, so the browser's own
                    download is both simpler and the only path that survives a
                    file too large to hold in memory.
                  */}
                  {file !== null ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={file.url} download>
                        {t('transfer.download')}
                      </a>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {isExportRunning(job)
                        ? `${String(job.progress_percentage)}%`
                        : t('transfer.noFile')}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}

function ImportPanel({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const upload = useUploadImport(workspaceId)
  const start = useImportWorkspace(workspaceId)

  const resource = upload.data

  return (
    <div className="space-y-4 py-2">
      <div className="grid gap-1">
        <Label htmlFor="import-file" className="text-sm">
          {t('transfer.chooseFile')}
        </Label>
        <Input
          id="import-file"
          type="file"
          accept=".zip"
          disabled={upload.isPending}
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file !== undefined) upload.mutate(file)
          }}
        />
        <p className="text-xs text-muted-foreground">{t('transfer.fileHint')}</p>
      </div>

      {upload.isPending && <Skeleton className="h-10" />}

      {/*
        Two steps rather than one, because the server makes them two: the upload
        answers with a resource that has been checked and is a real archive, and
        only then is there something to import. Doing both on one click would
        mean a file that failed its check and an import that was never started
        looking identical.
      */}
      {resource !== undefined && (
        <div className="flex items-center justify-between gap-2 rounded border p-2">
          <div className="min-w-0">
            <p className="truncate text-sm">{resource.name}</p>
            {resource.size != null && (
              <p className="text-xs text-muted-foreground">{byteSize(resource.size)}</p>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              size="sm"
              disabled={start.isPending}
              onClick={() => start.mutate(resource.id)}
            >
              <Upload />
              {t('transfer.import')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => upload.reset()}>
              <Trash2 />
              {t('action.cancel')}
            </Button>
          </div>
        </div>
      )}

      {start.isSuccess && (
        <p className="text-sm text-muted-foreground">{t('transfer.importing')}</p>
      )}
    </div>
  )
}
